/**
 * Grid Layout
 * CSS Grid-based layout system with design system tokens
 */

import React from "react";
import { SimpleGrid as ChakraSimpleGrid, Grid as ChakraGrid, GridProps as ChakraGridProps } from "@chakra-ui/react";
import { SpacingToken, resolveSpacing } from "design-system/foundation";
import { Spacing } from "design-system/foundation/Spacing";

// Branded types for Grid
declare const __grid_columns_brand: unique symbol;

export interface GridColumnsToken {
  readonly [__grid_columns_brand]: true;
  readonly _value: number | string;
}

// Factory function
const createGridColumnsToken = (value: number | string): GridColumnsToken => ({ _value: value } as GridColumnsToken);

export interface GridProps extends Omit<ChakraGridProps, "columns" | "gap" | "columnGap" | "rowGap"> {
  children: React.ReactNode;
  columns?: GridColumnsToken | number | { base?: number; md?: number; lg?: number; xl?: number };
  gap?: SpacingToken;
  columnGap?: SpacingToken;
  rowGap?: SpacingToken;
}

function GridComponent({
  children,
  columns,
  gap = Spacing.medium,
  columnGap,
  rowGap,
  ...props
}: GridProps) {
  const resolvedGap = resolveSpacing(gap);
  const resolvedColumnGap = columnGap ? resolveSpacing(columnGap) : undefined;
  const resolvedRowGap = rowGap ? resolveSpacing(rowGap) : undefined;

  // Handle responsive columns object
  if (typeof columns === "object" && columns !== null && !("_value" in columns)) {
    return (
      <ChakraSimpleGrid
        columns={columns}
        spacing={resolvedGap}
        spacingX={resolvedColumnGap}
        spacingY={resolvedRowGap}
        {...props}
      >
        {children}
      </ChakraSimpleGrid>
    );
  }

  // Handle branded token or number
  const columnValue = typeof columns === "number" ? columns
    : columns && "_value" in columns ? columns._value
      : undefined;

  if (typeof columnValue === "number") {
    return (
      <ChakraSimpleGrid
        columns={columnValue}
        spacing={resolvedGap}
        spacingX={resolvedColumnGap}
        spacingY={resolvedRowGap}
        {...props}
      >
        {children}
      </ChakraSimpleGrid>
    );
  }

  // Use CSS Grid for more complex layouts
  return (
    <ChakraGrid
      gap={resolvedGap}
      columnGap={resolvedColumnGap}
      rowGap={resolvedRowGap}
      {...props}
    >
      {children}
    </ChakraGrid>
  );
}

// Grid tokens
const gridTokens = {
  columns: {
    one: createGridColumnsToken(1),
    two: createGridColumnsToken(2),
    three: createGridColumnsToken(3),
    four: createGridColumnsToken(4),
    five: createGridColumnsToken(5),
    six: createGridColumnsToken(6),
    auto: createGridColumnsToken("auto"),
  } as const,
} as const;

// Public API - combines tokens and component
export const Grid = Object.assign(GridComponent, gridTokens);
